import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  className = '',
  maxVisiblePages = 5,
}) => {
  // Calculate visible page range
  const getPageRange = () => {
    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(currentPage - half, 1);
    const end = Math.min(start + maxVisiblePages - 1, totalPages);

    // Adjust if we're at the end
    if (end === totalPages) {
      start = Math.max(totalPages - maxVisiblePages + 1, 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const pages = getPageRange();

  if (totalPages <= 1) return null;

  return (
    <nav className={`flex items-center justify-between ${className}`}>
      {/* Previous Button */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`relative inline-flex items-center px-3 py-2 rounded-md text-sm font-medium ${
          currentPage === 1
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
        <span className="sr-only">Previous</span>
      </button>

      {/* Page Numbers */}
      <div className="hidden sm:flex space-x-1">
        {/* First Page (if not in visible range) */}
        {!pages.includes(1) && (
          <>
            <PageButton number={1} onClick={() => onPageChange(1)} />
            {pages[0] > 2 && <Ellipsis />}
          </>
        )}

        {/* Visible Pages */}
        {pages.map((page) => (
          <PageButton
            key={page}
            number={page}
            isActive={page === currentPage}
            onClick={() => onPageChange(page)}
          />
        ))}

        {/* Last Page (if not in visible range) */}
        {!pages.includes(totalPages) && (
          <>
            {pages[pages.length - 1] < totalPages - 1 && <Ellipsis />}
            <PageButton number={totalPages} onClick={() => onPageChange(totalPages)} />
          </>
        )}
      </div>

      {/* Mobile Compact Display */}
      <div className="flex sm:hidden text-sm text-gray-700">
        Page {currentPage} of {totalPages}
      </div>

      {/* Next Button */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`relative inline-flex items-center px-3 py-2 rounded-md text-sm font-medium ${
          currentPage === totalPages
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
        <span className="sr-only">Next</span>
      </button>
    </nav>
  );
};

// Sub-component for page buttons
const PageButton = ({ number, isActive = false, onClick }) => (
  <button
    onClick={onClick}
    className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
      isActive
        ? 'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
        : 'bg-white text-gray-700 hover:bg-gray-50'
    } rounded-md`}
  >
    {number}
  </button>
);

// Sub-component for ellipsis
const Ellipsis = () => (
  <span className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700">
    ...
  </span>
);

export default Pagination;